<template>
  <component
    :is="Component"
    v-bind="$attrs"
    class="qtm-data-table"
    :class="`bg-${backgroundColor} ${wrapHeader ? '' : 'header-no-wrap'}`"
    :headers="processedHeaders"
    :items-per-page="itemsPerPage"
    :items-per-page-options="itemsPerPageOptions"
    :page="page"
    :mobile="false"
    sort-asc-icon="mdi-menu-up"
    sort-desc-icon="mdi-menu-down"
    @update:options="onPagination"
  >
    <template v-slot:header.data-table-select="{ allSelected, selectAll, someSelected }">
      <v-checkbox
        base-color="mid-light-grey"
        color="interactive"
        density="compact"
        hide-details
        :indeterminate="someSelected && !allSelected"
        :model-value="allSelected"
        @update:model-value="selectAll"
      />
    </template>
    <template v-slot:item.data-table-select="{ internalItem, isSelected, toggleSelect }">
      <v-checkbox
        base-color="mid-light-grey"
        color="interactive"
        density="compact"
        hide-details
        :model-value="isSelected(internalItem)"
        @update:model-value="toggleSelect(internalItem)"
      />
    </template>
    <template v-slot:loading>
      <v-progress-circular color="secondary" indeterminate />
    </template>
    <template v-for="(_, name) in $slots" v-slot:[name]="slotData" :key="name">
      <slot v-bind="slotData" :key="name" :name="name" />
    </template>
    <template v-if="!showDefaultFooter" v-slot:bottom>
      <div />
    </template>
  </component>
</template>

<script setup lang="ts">
export interface Props {
  backgroundColor?: string
  headers?: any[]
  showDefaultFooter?: boolean
  itemsPerPage?: number
  itemsPerPageOptions?: number[]
  offset?: number
  serverSide?: boolean
  wrapHeader?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  backgroundColor: 'white',
  headers: () => [],
  itemsPerPage: 10000,
  itemsPerPageOptions: () => [25, 50, -1],
  offset: 0,
})

const emit = defineEmits(['on-pagination', 'update:items-per-page', 'update:offset'])

const page = computed(() => Number(props.offset / props.itemsPerPage) + 1)
const processedHeaders = computed(() => props.headers.map((header: any) => ({
  ...header,
  minWidth: header.minWidth ?? header.width,
})))

const Component = computed(() => (props.serverSide ? 'v-data-table-server' : 'v-data-table'))

const onPagination = (pagination: { itemsPerPage: number, page: number } ) => {
  if (pagination.itemsPerPage !== props.itemsPerPage) {
    emit('update:items-per-page', pagination.itemsPerPage)
  }

  const offset = (pagination.page - 1) * pagination.itemsPerPage
  if (offset !== props.offset) {
    emit('update:offset', offset)
  }

  emit('on-pagination')
}
</script>

<style lang="scss" scoped>
.qtm-data-table {
  overflow-wrap: anywhere;
  word-break: normal;
}
</style>

<style lang="scss">
.header-no-wrap th {
  white-space: nowrap !important;
}

.qtm-data-table th {
  color: rgb(var(--v-theme-mid-grey)) !important;
  font-size: 1rem !important;
  font-weight: 400 !important;
  height: 65px !important;  // For some reason this gives a height of 64px
}

.qtm-data-table td {
  font-size: 1rem !important;
  color: rgb(var(--v-theme-dark-grey)) !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 1px;  // Hack to respect the headers width attribute
  height: 69px !important;  // For some reason this gives a height of 68px
}

.qtm-data-table th,
.qtm-data-table td {
  padding-left: 8px !important;
  padding-right: 8px !important;
}

.qtm-data-table th:first-child,
.qtm-data-table td:first-child{
  padding-left: 16px !important;
}

.qtm-data-table th:last-child,
.qtm-data-table td:last-child{
  padding-right: 16px !important;
}
</style>
